import React, { useState, useEffect, useMemo, useCallback } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import MarksCardRenderer from '../../MarksCardRenderer';
import ReportCardStudentDrawer from './ReportCardStudentDrawer';
import ReportCardPreview from './ReportCardPreview';
import ReportCardPagination from './ReportCardPagination';
import { reportCardService, studentService, ReportCardTemplateService } from '../../../services/index.js';
import { CheckSquare, Square, Download, RefreshCw } from 'lucide-react';

const STANDARD_SCHOOL_CLASSES = ['LKG', 'UKG', 'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5', 'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10'];

const TEMPLATE_PRESET_CARDS = [
  { key: 'classic_academic', name: 'Classic Academic', desc: 'Simple formal subject-wise progress report with gold crest styling', badge: 'Standard' },
  { key: 'modern_school', name: 'Modern School', desc: 'Continuous assessment card with score badges and ribbon header', badge: 'Continuous FA+SA' },
  { key: 'term_based_progress', name: 'Term Based', desc: 'Periodic tests (PT), Notebook, SEA & Term exam evaluation matrix', badge: 'CBSE Pattern' },
  { key: 'annual_progress', name: 'Annual Cumulative', desc: 'Year-end comprehensive report combining Term 1, Term 2 & promotion', badge: 'Annual' },
  { key: 'primary_progress', name: 'Primary Progress', desc: 'Child-friendly progress report with domain ratings and attendance meter', badge: 'Class 1-5' },
  { key: 'kindergarten_progress', name: 'Kindergarten', desc: 'Qualitative developmental ratings across language, motor & social domains', badge: 'Pre-Primary' }
];

const cleanStr = (s) => (s || '').toString().toLowerCase().trim();
const cleanSec = (s) => cleanStr(s).replace(/^section\s*/i, '').trim();

const getStudentClass = (s) => {
  if (!s) return '';
  return s.className || s.academicLevel || s.studentClass || s.class || s.degree || s.standard || s.currentClass || '';
};

const normalizeClassName = (name) => {
  if (!name) return '';
  return name
    .toString()
    .toLowerCase()
    .replace(/^class\s*/i, '')
    .replace(/\s*standard$/i, '')
    .replace(/\s*std$/i, '')
    .replace(/(st|nd|rd|th)$/i, '')
    .replace(/[^a-z0-9]/g, '')
    .trim();
};

const matchClassNames = (classA, classB) => {
  if (!classA && !classB) return true;
  if (!classA || !classB) return false;
  const a = cleanStr(classA);
  const b = cleanStr(classB);
  if (a === b) return true;
  const normA = normalizeClassName(a);
  const normB = normalizeClassName(b);
  return normA.length > 0 && normA === normB;
};

const matchSection = (studentSec, filterSec) => {
  if (!filterSec || filterSec === 'ALL') return true;
  const normS = cleanSec(studentSec);
  const normF = cleanSec(filterSec);
  if (!normS && normF === 'all') return true;
  return normS === normF;
};

const ReportCardWorkspace = ({
  orgData,
  students: propStudents = [],
  classes = [],
  tests = [],
  activeAcademicYear,
  availableAcademicYears = [],
  examTypeConfigs = [],
  onOpenDesigner,
  showSuccessNotification,
  showErrorNotification
}) => {
  // Scopes
  const initialYear = activeAcademicYear || orgData?.academicYear || orgData?.activeAcademicYear || '2026-27';
  const [academicYear, setAcademicYear] = useState(initialYear);

  const availableClasses = useMemo(() => {
    return classes && classes.length > 0
      ? [...new Set(classes.map(c => c.className || c.name))].filter(Boolean)
      : STANDARD_SCHOOL_CLASSES;
  }, [classes]);

  const [selectedClass, setSelectedClass] = useState(
    (availableClasses && availableClasses.length > 0) ? availableClasses[0] : 'LKG'
  );
  const [selectedSection, setSelectedSection] = useState('ALL');

  // Synchronize selectedClass whenever availableClasses loads
  useEffect(() => {
    if (availableClasses.length > 0 && !availableClasses.includes(selectedClass)) {
      setSelectedClass(availableClasses[0]);
    }
  }, [availableClasses, selectedClass]);

  // Dynamic academic year list
  const academicYearOptions = useMemo(() => {
    if (availableAcademicYears && availableAcademicYears.length > 0) {
      return availableAcademicYears;
    }
    const years = new Set();
    if (academicYear) years.add(academicYear);
    const curYear = new Date().getFullYear();
    for (let i = -2; i <= 2; i++) {
      const y = curYear + i;
      years.add(`${y}-${(y + 1).toString().slice(-2)}`);
    }
    return Array.from(years).sort().reverse();
  }, [availableAcademicYears, academicYear]);

  // Report Card Mode: 'SINGLE' or 'CONSOLIDATED'
  const [reportMode, setReportMode] = useState('CONSOLIDATED'); // 'SINGLE' | 'CONSOLIDATED'
  const [singleExamTitle, setSingleExamTitle] = useState('FA-1');
  const [selectedExams, setSelectedExams] = useState(['FA-1', 'FA-2', 'SA-1', 'SA-2']);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState('classic_academic');
  const [dbTemplates, setDbTemplates] = useState([]);

  // Data States
  const [reportCards, setReportCards] = useState([]);
  const [allStudents, setAllStudents] = useState(propStudents || []);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Preview & Drawer States
  const [previewReportCard, setPreviewReportCard] = useState(null);
  const [drawerReportCard, setDrawerReportCard] = useState(null);
  const [bulkProgress, setBulkProgress] = useState({ generating: false, current: 0, total: 0, msg: '' });

  useEffect(() => {
    if (activeAcademicYear) {
      setAcademicYear(activeAcademicYear);
    }
  }, [activeAcademicYear]);

  /**
   * DB-driven sections for the selected class.
   * Derived from classes prop — never shows A/B/C/D unless those sections
   * actually exist in the database for the selected class.
   */
  const sectionOptions = useMemo(() => {
    if (!classes || classes.length === 0) return [];
    const relevant = classes.filter(c => matchClassNames(c.className || c.name, selectedClass));
    const unique = [...new Set(relevant.map(c => c.section || c.sectionName).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return unique;
  }, [classes, selectedClass]);

  // Distinct exam titles available in this school for this class and academic year
  // Merges school exam types catalog and actual announced tests so all configured
  // exams (FA-1, FA-2, SA-1, FA-3, FA-4, SA-2, etc.) can be included in consolidated marks card
  const availableExamTitles = useMemo(() => {
    const normalizeAcademicYear = (yr) => {
      if (!yr) return yr;
      const match = yr.match(/^(\d{4})-(\d{2})$/);
      if (match) {
        const century = match[1].slice(0, 2);
        return `${match[1]}-${century}${match[2]}`;
      }
      return yr;
    };

    const matching = tests.filter(t => {
      // Exclude teacher classroom tests — only official school exams
      if (t.createdByRole === 'TEACHER' && t.teacherId && t.teacherId !== 'ADMIN' && t.testType !== 'EXAM' && t.testType !== 'SCHOOL_EXAM') {
        return false;
      }
      const tYear = (t.description || t.academicYear || '').trim();
      if (tYear && /^\d{4}-\d{2,4}$/.test(tYear)) {
        if (normalizeAcademicYear(tYear) !== normalizeAcademicYear(academicYear)) return false;
      }
      return !selectedClass || matchClassNames(t.className, selectedClass);
    });

    const announcedTitles = matching.map(t => t.title).filter(Boolean);
    const configTitles = (examTypeConfigs || []).map(e => e.examCode).filter(Boolean);
    const combined = [...new Set([...configTitles, ...announcedTitles])];

    // Sort by examTypeConfig sortOrder when config is available
    if (examTypeConfigs && examTypeConfigs.length > 0) {
      const orderMap = {};
      examTypeConfigs.forEach((et, idx) => { orderMap[et.examCode] = et.sortOrder || idx + 1; });
      combined.sort((a, b) => {
        const oa = orderMap[a] !== undefined ? orderMap[a] : 9999;
        const ob = orderMap[b] !== undefined ? orderMap[b] : 9999;
        return oa - ob;
      });
      return combined;
    }

    return combined.length > 0 ? combined : ['FA-1', 'FA-2', 'SA-1', 'SA-2'];
  }, [tests, academicYear, selectedClass, examTypeConfigs]);

  // Sync singleExamTitle when available exams change
  useEffect(() => {
    if (availableExamTitles.length > 0 && !availableExamTitles.includes(singleExamTitle)) {
      setSingleExamTitle(availableExamTitles[0]);
    }
  }, [availableExamTitles, singleExamTitle]);

  // Auto-sync selectedExams so any configured exam types (FA-1, FA-2, SA-1, FA-3, FA-4, SA-2, etc.) are included
  useEffect(() => {
    if (availableExamTitles.length > 0) {
      setSelectedExams(prev => {
        if (!prev || prev.length === 0) return availableExamTitles;
        const isDefaultPreset = prev.length === 4 && ['FA-1', 'FA-2', 'SA-1', 'SA-2'].every(e => prev.includes(e));
        if (isDefaultPreset && availableExamTitles.length > 4) {
          return availableExamTitles;
        }
        const valid = prev.filter(e => availableExamTitles.includes(e));
        return valid.length > 0 ? valid : availableExamTitles;
      });
    }
  }, [availableExamTitles]);

  // Load students self-contained
  const fetchStudentsDirectly = useCallback(async () => {
    try {
      const orgId = orgData?.orgId || localStorage.getItem('orgId') || 'SCH-02';
      const res = await studentService.getStudents(orgId);
      if (Array.isArray(res)) setAllStudents(res);
      else if (res && Array.isArray(res.students)) setAllStudents(res.students);
      else if (res && Array.isArray(res.data)) setAllStudents(res.data);
    } catch (err) {
      console.error('Failed to load students in ReportCardWorkspace:', err);
    }
  }, [orgData?.orgId]);

  useEffect(() => {
    if (!propStudents || propStudents.length === 0) {
      fetchStudentsDirectly();
    } else {
      setAllStudents(propStudents);
    }
  }, [propStudents, fetchStudentsDirectly]);

  // Load organization templates and discover active default template
  useEffect(() => {
    let isMounted = true;
    const loadOrgTemplates = async () => {
      const orgId = orgData?.orgId || localStorage.getItem('orgId') || 'SCH-02';
      try {
        const res = await ReportCardTemplateService.getTemplatesByOrg(orgId);
        const templates = Array.isArray(res) ? res : (res?.data || []);
        if (isMounted) {
          setDbTemplates(templates);
          // Prioritize organization's custom active default template, otherwise system default
          const defaultTpl = templates.find(t => t.isDefault && !t.isSystemPreset) || templates.find(t => t.isDefault);
          if (defaultTpl?.templateKey) {
            setSelectedTemplateKey(defaultTpl.templateKey);
          }
        }
      } catch (err) {
        console.error('Failed to load organization templates in ReportCardWorkspace:', err);
      }
    };
    loadOrgTemplates();
    return () => { isMounted = false; };
  }, [orgData?.orgId]);

  // Merge presets with database configurations and custom templates
  const displayTemplates = useMemo(() => {
    const list = TEMPLATE_PRESET_CARDS.map(preset => {
      const dbMatch = dbTemplates.find(t => t.templateKey === preset.key);
      return {
        ...preset,
        name: dbMatch?.templateName || preset.name,
        isDefault: dbMatch ? !!dbMatch.isDefault : preset.key === 'classic_academic',
        isCustom: dbMatch ? !dbMatch.isSystemPreset : false
      };
    });

    dbTemplates.forEach(t => {
      if (!list.some(item => item.key === t.templateKey)) {
        list.push({
          key: t.templateKey,
          name: t.templateName,
          desc: t.description || 'Custom school report card layout',
          badge: t.isDefault ? 'School Default' : 'Custom',
          isDefault: !!t.isDefault,
          isCustom: true
        });
      }
    });

    return list;
  }, [dbTemplates]);

  // Load existing report cards
  const loadReportCards = useCallback(async () => {
    if (!selectedClass) return;
    try {
      const termParam = reportMode === 'SINGLE' ? singleExamTitle : 'Consolidated';
      const params = {
        academicYear,
        className: selectedClass,
        semester: termParam
      };
      const data = await reportCardService.listByOrg(params);
      setReportCards(Array.isArray(data) ? data : []);
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Failed to load report cards:', err);
      setReportCards([]);
    }
  }, [academicYear, selectedClass, reportMode, singleExamTitle]);

  useEffect(() => {
    loadReportCards();
  }, [loadReportCards, selectedSection]);

  // Filter class students
  const classStudents = useMemo(() => {
    return allStudents.filter(s => {
      const sClass = getStudentClass(s);
      const matchClass = !selectedClass || matchClassNames(sClass, selectedClass);
      const matchSec = matchSection(s.section || s.sectionName, selectedSection);
      return matchClass && matchSec;
    });
  }, [allStudents, selectedClass, selectedSection]);

  // Search filtered students
  const filteredStudents = useMemo(() => {
    return classStudents.filter(s => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      const nameMatch = (s.name || s.studentName || '').toLowerCase().includes(q);
      const idMatch = (s.studentId || '').toLowerCase().includes(q);
      const rollMatch = (s.rollNo || s.rollNumber || '').toString().toLowerCase().includes(q);
      return nameMatch || idMatch || rollMatch;
    });
  }, [classStudents, searchQuery]);

  const paginatedStudents = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredStudents.slice(start, start + pageSize);
  }, [filteredStudents, currentPage, pageSize]);

  // Toggle Exam checkbox for Consolidated Mode
  const handleToggleExam = (title) => {
    if (selectedExams.includes(title)) {
      if (selectedExams.length === 1) {
        alert('Please keep at least one exam selected.');
        return;
      }
      setSelectedExams(selectedExams.filter(t => t !== title));
    } else {
      setSelectedExams([...selectedExams, title]);
    }
  };

  // Compile Single Report Card
  const handleCompileSingle = async (studentId) => {
    try {
      const termParam = reportMode === 'SINGLE' ? singleExamTitle : 'Consolidated';
      const payload = {
        studentId,
        academicYear,
        semester: termParam,
        term: termParam,
        templateKey: selectedTemplateKey,
        includedExams: reportMode === 'CONSOLIDATED' ? selectedExams : [singleExamTitle]
      };

      const res = await reportCardService.generateReportCard(payload);
      if (res && res.success) {
        showSuccessNotification('Report Card Ready', `Generated report card for student ${studentId}.`);
        loadReportCards();
      } else {
        showErrorNotification('Generation Failed', res.error || 'Failed to generate report card.');
      }
    } catch (err) {
      showErrorNotification('Error', err.message);
    }
  };

  // Compile Bulk Report Cards
  const handleCompileAll = async () => {
    if (classStudents.length === 0) return;
    if (!window.confirm(`Compile report cards for all ${classStudents.length} students in ${selectedClass}?`)) return;

    setBulkProgress({ generating: true, current: 0, total: classStudents.length, msg: 'Compiling report cards...' });
    try {
      let count = 0;
      let lastError = null;
      const termParam = reportMode === 'SINGLE' ? singleExamTitle : 'Consolidated';

      for (let i = 0; i < classStudents.length; i++) {
        const stu = classStudents[i];
        setBulkProgress(prev => ({ ...prev, current: i + 1, msg: `Compiling ${stu.name || stu.studentId}...` }));

        const payload = {
          studentId: stu.studentId,
          academicYear,
          semester: termParam,
          term: termParam,
          templateKey: selectedTemplateKey,
          includedExams: reportMode === 'CONSOLIDATED' ? selectedExams : [singleExamTitle]
        };

        try {
          const res = await reportCardService.generateReportCard(payload);
          if (res && res.success) {
            count++;
          } else if (res && res.error) {
            lastError = res.error;
          }
        } catch (err) {
          lastError = err.message;
        }
      }

      if (count > 0) {
        showSuccessNotification('Bulk Compilation Finished', `Successfully compiled ${count} report cards.`);
      } else {
        showErrorNotification('Compilation Failed', lastError || 'No report cards could be compiled. Please check if marks have been entered.');
      }
      loadReportCards();
    } catch (err) {
      showErrorNotification('Bulk Error', err.message);
    } finally {
      setBulkProgress({ generating: false, current: 0, total: 0, msg: '' });
    }
  };

  // Batch update all compiled report cards to the currently selected template
  const handleApplyTemplateToAll = async () => {
    if (reportCards.length === 0) return;
    const tName = displayTemplates.find(t => t.key === selectedTemplateKey)?.name || selectedTemplateKey;
    if (!window.confirm(`Update all ${reportCards.length} compiled report cards in ${selectedClass} to the "${tName}" format?`)) return;

    try {
      showSuccessNotification('Updating Template', `Applying "${tName}" to ${reportCards.length} report cards...`);
      await reportCardService.batchUpdateTemplate({
        templateKey: selectedTemplateKey,
        academicYear,
        className: selectedClass,
        term: reportMode === 'SINGLE' ? singleExamTitle : 'Consolidated'
      });
      showSuccessNotification('Format Applied', `All ${reportCards.length} report cards now use "${tName}" format!`);
      loadReportCards();
    } catch (err) {
      showErrorNotification('Error', err.message);
    }
  };

  // Single PDF Download
  const handleDownloadSinglePDF = async (rc, formatOverride) => {
    const templateToUse = (typeof formatOverride === 'string' && formatOverride) ? formatOverride : (selectedTemplateKey || 'classic_academic');
    try {
      showSuccessNotification('Generating PDF', `Preparing marks card PDF...`);
      const details = await reportCardService.getReportCardById(rc.id);

      const container = document.createElement('div');
      container.style.cssText = 'position:fixed;top:-9999px;left:-9999px;z-index:-1;';
      document.body.appendChild(container);

      const { createRoot } = await import('react-dom/client');
      const el = document.createElement('div');
      container.appendChild(el);

      const root = createRoot(el);
      await new Promise(resolve => {
        root.render(
          <MarksCardRenderer
            reportCard={details.reportCard}
            entries={details.entries}
            branding={details.branding || orgData || {}}
            institutionName={details.organizationInfo?.orgName || orgData?.orgName}
            institutionAddress={details.organizationInfo?.address || orgData?.address}
            id="single-rc-download-workspace"
            template={templateToUse}
            gradingScale={details.gradingScale || []}
          />
        );
        setTimeout(resolve, 400);
      });

      const captureEl = document.getElementById('single-rc-download-workspace');
      const canvas = await html2canvas(captureEl, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF('p', 'mm', 'a4');
      pdf.addImage(imgData, 'PNG', 0, 0, 210, 297);
      pdf.save(`${rc.student?.name || 'student'}_${reportMode}_report_card.pdf`);

      root.unmount();
      container.removeChild(el);
      document.body.removeChild(container);
    } catch (err) {
      console.error(err);
      showErrorNotification('PDF Error', err.message);
    }
  };

  // Bulk PDF Download
  const handleDownloadBulkPDF = async () => {
    const targets = selectedIds.size > 0
      ? reportCards.filter(rc => selectedIds.has(rc.id))
      : reportCards;

    if (targets.length === 0) {
      alert('No compiled report cards available to download.');
      return;
    }

    setBulkProgress({ generating: true, current: 0, total: targets.length, msg: 'Preparing bulk PDF...' });
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const container = document.createElement('div');
      container.style.cssText = 'position:fixed;top:-9999px;left:-9999px;z-index:-1;';
      document.body.appendChild(container);

      const { createRoot } = await import('react-dom/client');

      for (let i = 0; i < targets.length; i++) {
        const rc = targets[i];
        setBulkProgress(prev => ({ ...prev, current: i + 1, msg: `Rendering ${rc.student?.name || rc.reportCardNumber}...` }));

        const details = await reportCardService.getReportCardById(rc.id);
        const el = document.createElement('div');
        container.appendChild(el);

        const root = createRoot(el);
        await new Promise(resolve => {
          root.render(
            <MarksCardRenderer
              reportCard={details.reportCard}
              entries={details.entries}
              branding={details.branding || orgData || {}}
              institutionName={details.organizationInfo?.orgName || orgData?.orgName}
              institutionAddress={details.organizationInfo?.address || orgData?.address}
              id={`bulk-rc-workspace-${rc.id}`}
              template={selectedTemplateKey}
              gradingScale={details.gradingScale || []}
            />
          );
          setTimeout(resolve, 350);
        });

        const captureEl = document.getElementById(`bulk-rc-workspace-${rc.id}`);
        const canvas = await html2canvas(captureEl, { scale: 2, useCORS: true });
        const imgData = canvas.toDataURL('image/png');

        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, 0, 210, 297);

        root.unmount();
        container.removeChild(el);
      }

      document.body.removeChild(container);
      pdf.save(`${selectedClass}_${reportMode}_Bulk_Report_Cards.pdf`);
      showSuccessNotification('Bulk PDF Ready', `Downloaded ${targets.length} report cards.`);
    } catch (err) {
      console.error(err);
      showErrorNotification('Bulk Error', err.message);
    } finally {
      setBulkProgress({ generating: false, current: 0, total: 0, msg: '' });
    }
  };

  // Status updates
  const handlePublishSingle = async (rcId) => {
    try {
      const res = await reportCardService.updateStatus(rcId, 'PUBLISHED');
      if (res && res.success) {
        showSuccessNotification('Published', 'Report card is now published and available.');
        loadReportCards();
      }
    } catch (err) {
      showErrorNotification('Error', err.message);
    }
  };

  const handleToggleSelect = (rcId) => {
    const next = new Set(selectedIds);
    if (next.has(rcId)) next.delete(rcId);
    else next.add(rcId);
    setSelectedIds(next);
  };

  const handleToggleSelectAll = () => {
    if (selectedIds.size === paginatedStudents.length) {
      setSelectedIds(new Set());
    } else {
      const ids = new Set();
      paginatedStudents.forEach(s => {
        const rc = reportCards.find(r => r.student?.studentId === s.studentId);
        if (rc) ids.add(rc.id);
      });
      setSelectedIds(ids);
    }
  };

  if (previewReportCard) {
    return (
      <ReportCardPreview
        reportCard={previewReportCard}
        initialFormat={selectedTemplateKey}
        orgData={orgData}
        onBack={() => setPreviewReportCard(null)}
        onDownloadPDF={handleDownloadSinglePDF}
        onPublish={handlePublishSingle}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      
      {/* 1. Header & Scope Selector */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        padding: '18px 24px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.3px' }}>
            Report Cards
          </h3>
          <p style={{ margin: '3px 0 0 0', fontSize: '13px', color: '#64748b' }}>
            Create, preview, and print official student report cards.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          {/* Academic Year */}
          <select
            value={academicYear}
            onChange={(e) => setAcademicYear(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '7px', border: '1.5px solid #cbd5e1', fontSize: '13px', fontWeight: '700', color: '#1e293b', background: '#ffffff' }}
          >
            {academicYearOptions.map(yr => (
              <option key={yr} value={yr}>{yr} Session</option>
            ))}
          </select>

          {/* Class */}
          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
            style={{ padding: '8px 14px', borderRadius: '7px', border: '1.5px solid #cbd5e1', fontSize: '13px', fontWeight: '700', color: '#1e293b', background: '#ffffff' }}
          >
            {availableClasses.map(cls => (
              <option key={cls} value={cls}>{cls}</option>
            ))}
          </select>

          {/* Section */}
          <select
            value={selectedSection}
            onChange={(e) => setSelectedSection(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '7px', border: '1.5px solid #cbd5e1', fontSize: '13px', fontWeight: '700', color: '#1e293b', background: '#ffffff' }}
          >
            <option value="ALL">All Sections</option>
            {sectionOptions.length > 0 ? (
              sectionOptions.map(sec => (
                <option key={sec} value={sec}>Section {sec}</option>
              ))
            ) : null}
          </select>
        </div>
      </div>

      {/* 2. Central Question: WHAT REPORT CARD DO YOU WANT TO GENERATE? */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        padding: '22px 24px',
        border: '1.5px solid #bfdbfe',
        boxShadow: '0 2px 8px rgba(37,99,235,0.06)'
      }}>
        <div style={{ marginBottom: '16px' }}>
          <span style={{ fontSize: '11px', fontWeight: '800', color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
            Report Card Setup
          </span>
          <h4 style={{ margin: '3px 0 0 0', fontSize: '17px', fontWeight: '800', color: '#0f172a' }}>
            What report card do you want to generate for {selectedClass}?
          </h4>
        </div>

        {/* Mode Selector Radio Options */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '18px' }}>
          {/* Option A: Single Exam */}
          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              padding: '14px 16px',
              borderRadius: '10px',
              border: reportMode === 'SINGLE' ? '2px solid #1e3a8a' : '1px solid #cbd5e1',
              background: reportMode === 'SINGLE' ? '#eff6ff' : '#ffffff',
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
          >
            <input
              type="radio"
              name="reportMode"
              checked={reportMode === 'SINGLE'}
              onChange={() => setReportMode('SINGLE')}
              style={{ marginTop: '3px', accentColor: '#1e3a8a', width: '16px', height: '16px' }}
            />
            <div>
              <strong style={{ fontSize: '14px', color: '#0f172a', display: 'block' }}>
                Single Exam Report Card
              </strong>
              <span style={{ fontSize: '12.5px', color: '#64748b', marginTop: '2px', display: 'block' }}>
                Generate a marks card for one specific examination (e.g. FA-1 or SA-1 only).
              </span>
            </div>
          </label>

          {/* Option B: Consolidated */}
          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              padding: '14px 16px',
              borderRadius: '10px',
              border: reportMode === 'CONSOLIDATED' ? '2px solid #1e3a8a' : '1px solid #cbd5e1',
              background: reportMode === 'CONSOLIDATED' ? '#eff6ff' : '#ffffff',
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
          >
            <input
              type="radio"
              name="reportMode"
              checked={reportMode === 'CONSOLIDATED'}
              onChange={() => setReportMode('CONSOLIDATED')}
              style={{ marginTop: '3px', accentColor: '#1e3a8a', width: '16px', height: '16px' }}
            />
            <div>
              <strong style={{ fontSize: '14px', color: '#0f172a', display: 'block' }}>
                Consolidated / Annual Report Card
              </strong>
              <span style={{ fontSize: '12.5px', color: '#64748b', marginTop: '2px', display: 'block' }}>
                Combine multiple exams (e.g. FA1 + FA2 + SA1 + SA2) into one single marks card.
              </span>
            </div>
          </label>
        </div>

        {/* Dynamic Controls based on chosen Mode */}
        {reportMode === 'SINGLE' ? (
          <div style={{ background: '#f8fafc', padding: '16px 20px', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '13px', fontWeight: '700', color: '#334155' }}>
              Select Examination:
            </label>
            <select
              value={singleExamTitle}
              onChange={(e) => setSingleExamTitle(e.target.value)}
              style={{ padding: '8px 14px', borderRadius: '7px', border: '1.5px solid #cbd5e1', fontSize: '13.5px', fontWeight: '700', background: '#ffffff', minWidth: '180px' }}
            >
              {availableExamTitles.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <span style={{ fontSize: '12px', color: '#64748b' }}>
              Columns will show: <strong>Subject | Max Marks | Marks Obtained | Percentage | Grade</strong>
            </span>
          </div>
        ) : (
          <div style={{ background: '#f8fafc', padding: '16px 20px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <label style={{ fontSize: '13px', fontWeight: '700', color: '#334155' }}>
                Select Exams to Include in the Consolidated Marks Card:
              </label>
              <span style={{ fontSize: '11.5px', color: '#64748b', fontWeight: '600' }}>
                Selected exams automatically become columns
              </span>
            </div>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {availableExamTitles.map(examTitle => {
                const checked = selectedExams.includes(examTitle);
                return (
                  <button
                    key={examTitle}
                    type="button"
                    onClick={() => handleToggleExam(examTitle)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: checked ? '2px solid #1e3a8a' : '1px solid #cbd5e1',
                      background: checked ? '#ffffff' : '#f1f5f9',
                      color: checked ? '#1e3a8a' : '#64748b',
                      fontSize: '13px',
                      fontWeight: '800',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {checked ? <CheckSquare size={16} color="#1e3a8a" /> : <Square size={16} color="#94a3b8" />}
                    {examTitle}
                  </button>
                );
              })}
            </div>

            <div style={{ marginTop: '12px', fontSize: '12px', color: '#475569', background: '#ffffff', padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
              Preview Columns: <strong>Subject</strong>
              {selectedExams.map(e => <span key={e}> • <strong>{e}</strong></span>)}
              <span> • <strong>Total</strong> • <strong>%</strong> • <strong>Grade</strong></span>
            </div>
          </div>
        )}
      </div>

      {/* 3. Choose Report Card Template (Card Layout) */}
      <div style={{ background: '#ffffff', borderRadius: '12px', padding: '22px 24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>
              Report Card Templates
            </h4>
            <p style={{ margin: '2px 0 0 0', fontSize: '12.5px', color: '#64748b' }}>
              Choose visual layout style for printing and PDF generation.
            </p>
          </div>

          {onOpenDesigner && (
            <button
              type="button"
              onClick={onOpenDesigner}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'transparent',
                border: '1px solid #cbd5e1',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '700',
                color: '#475569',
                cursor: 'pointer'
              }}
            >
              Advanced Template Designer →
            </button>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
          {displayTemplates.map(card => {
            const isSelected = selectedTemplateKey === card.key;
            return (
              <div
                key={card.key}
                onClick={() => setSelectedTemplateKey(card.key)}
                style={{
                  border: isSelected ? '2px solid #1e3a8a' : card.isDefault ? '2px solid #f59e0b' : '1px solid #e2e8f0',
                  borderRadius: '10px',
                  padding: '14px',
                  background: isSelected ? '#f8fafc' : '#ffffff',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  boxShadow: isSelected ? '0 2px 8px rgba(30,58,138,0.1)' : 'none',
                  transition: 'all 0.15s ease'
                }}
              >
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', gap: '6px' }}>
                    <span style={{ fontSize: '13.5px', fontWeight: '800', color: '#0f172a' }}>{card.name}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {card.isDefault && (
                        <span style={{ background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a', borderRadius: '4px', padding: '1px 5px', fontSize: '10px', fontWeight: '800' }}>
                          ⭐ Default
                        </span>
                      )}
                      {isSelected && (
                        <span style={{ background: '#1e3a8a', color: '#ffffff', borderRadius: '50%', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px' }}>
                          ✓
                        </span>
                      )}
                    </div>
                  </div>
                  <p style={{ margin: 0, fontSize: '11.5px', color: '#64748b', lineHeight: '1.4' }}>
                    {card.desc}
                  </p>
                </div>

                <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '10.5px', fontWeight: '700', color: '#475569', background: '#f1f5f9', padding: '2px 7px', borderRadius: '4px' }}>
                    {card.badge}
                  </span>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: isSelected ? '#1e3a8a' : '#64748b' }}>
                    {isSelected ? 'Active Style' : 'Select'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 4. Student Records & Action Toolbar */}
      <div style={{ background: '#ffffff', borderRadius: '12px', padding: '22px 24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
        
        {/* Bulk Action Bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              type="text"
              placeholder="Search student by name or roll..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: '7px', border: '1px solid #cbd5e1', fontSize: '13px', width: '220px' }}
            />
            <span style={{ fontSize: '12.5px', color: '#64748b', fontWeight: '600' }}>
              {classStudents.length} Students in {selectedClass}
            </span>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              onClick={handleCompileAll}
              disabled={bulkProgress.generating}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                borderRadius: '7px',
                border: 'none',
                background: '#1e3a8a',
                color: '#ffffff',
                fontSize: '13px',
                fontWeight: '700',
                cursor: bulkProgress.generating ? 'not-allowed' : 'pointer'
              }}
            >
              <RefreshCw size={14} /> Compile All Reports
            </button>

            {reportCards.length > 0 && (
              <button
                type="button"
                onClick={handleApplyTemplateToAll}
                disabled={bulkProgress.generating}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  borderRadius: '7px',
                  border: '1.5px solid #3b82f6',
                  background: '#eff6ff',
                  color: '#1d4ed8',
                  fontSize: '13px',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
                title="Update all compiled cards in this class to use the selected template format"
              >
                <CheckSquare size={14} /> Apply Format to All ({reportCards.length})
              </button>
            )}

            <button
              type="button"
              onClick={handleDownloadBulkPDF}
              disabled={bulkProgress.generating || reportCards.length === 0}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                borderRadius: '7px',
                border: '1px solid #cbd5e1',
                background: '#ffffff',
                color: '#1e293b',
                fontSize: '13px',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              <Download size={14} /> Bulk PDF ({reportCards.length})
            </button>
          </div>
        </div>

        {/* Bulk Progress Indicator */}
        {bulkProgress.generating && (
          <div style={{ padding: '12px 18px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', color: '#1e3a8a', fontSize: '13px', fontWeight: '700', marginBottom: '14px' }}>
            ⏳ {bulkProgress.msg} ({bulkProgress.current} / {bulkProgress.total})
          </div>
        )}

        {/* Table */}
        {classStudents.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b', border: '1px dashed #cbd5e1', borderRadius: '8px' }}>
            No students enrolled in {selectedClass}.
          </div>
        ) : (
          <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', textAlign: 'left' }}>
                  <th style={{ padding: '10px 14px', width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.size === paginatedStudents.length && paginatedStudents.length > 0}
                      onChange={handleToggleSelectAll}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                  <th style={{ padding: '10px 14px', fontWeight: '700' }}>Student Name</th>
                  <th style={{ padding: '10px 14px', width: '100px', fontWeight: '700' }}>Roll No</th>
                  <th style={{ padding: '10px 14px', width: '100px', textAlign: 'center', fontWeight: '700' }}>Status</th>
                  <th style={{ padding: '10px 14px', width: '90px', textAlign: 'center', fontWeight: '700' }}>Marks</th>
                  <th style={{ padding: '10px 14px', width: '80px', textAlign: 'center', fontWeight: '700' }}>Grade</th>
                  <th style={{ padding: '10px 14px', width: '220px', textAlign: 'right', fontWeight: '700' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedStudents.map((stu, idx) => {
                  const rc = reportCards.find(r => r.student?.studentId === stu.studentId);
                  const isCompiled = Boolean(rc);
                  const isPublished = rc?.status === 'PUBLISHED' || rc?.status === 'LOCKED';

                  return (
                    <tr
                      key={stu.studentId || idx}
                      style={{
                        borderBottom: '1px solid #f1f5f9',
                        background: rc && selectedIds.has(rc.id) ? '#eff6ff' : idx % 2 === 0 ? '#ffffff' : '#fafafa'
                      }}
                    >
                      <td style={{ padding: '10px 14px' }}>
                        {rc ? (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(rc.id)}
                            onChange={() => handleToggleSelect(rc.id)}
                            style={{ cursor: 'pointer' }}
                          />
                        ) : (
                          <span style={{ color: '#cbd5e1' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <strong style={{ color: '#0f172a' }}>{stu.name || stu.studentName}</strong>
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>ID: {stu.studentId}</div>
                      </td>
                      <td style={{ padding: '10px 14px', color: '#475569', fontWeight: '600' }}>
                        {stu.rollNo || stu.rollNumber || '—'}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                        {isCompiled ? (
                          <span style={{
                            padding: '3px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '800',
                            background: isPublished ? '#f0fdf4' : '#eff6ff',
                            color: isPublished ? '#16a34a' : '#2563eb'
                          }}>
                            {rc.status}
                          </span>
                        ) : (
                          <span style={{ padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', background: '#fef2f2', color: '#dc2626' }}>
                            Not Compiled
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'center', fontWeight: '700', color: '#1e293b' }}>
                        {isCompiled ? `${rc.obtainedMarks}/${rc.totalMarks}` : '—'}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                        {isCompiled ? (
                          <span style={{
                            padding: '2px 7px',
                            borderRadius: '6px',
                            fontSize: '11.5px',
                            fontWeight: '800',
                            background: rc.result === 'PASS' ? '#f0fdf4' : '#fef2f2',
                            color: rc.result === 'PASS' ? '#16a34a' : '#dc2626'
                          }}>
                            {rc.overallGrade || '—'}
                          </span>
                        ) : '—'}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                          {isCompiled ? (
                            <>
                              <button
                                type="button"
                                onClick={() => setPreviewReportCard(rc)}
                                style={{ padding: '5px 10px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', color: '#1e3a8a', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}
                              >
                                Preview
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDownloadSinglePDF(rc)}
                                style={{ padding: '5px 8px', background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '6px', color: '#334155', fontSize: '12px', cursor: 'pointer' }}
                                title="Download PDF"
                              >
                                <Download size={13} />
                              </button>
                              {!isPublished && (
                                <button
                                  type="button"
                                  onClick={() => handlePublishSingle(rc.id)}
                                  style={{ padding: '5px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', color: '#16a34a', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}
                                >
                                  Publish
                                </button>
                              )}
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleCompileSingle(stu.studentId)}
                              style={{ padding: '5px 12px', background: '#1e3a8a', border: 'none', borderRadius: '6px', color: '#ffffff', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}
                            >
                              Compile
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <ReportCardPagination
          currentPage={currentPage}
          pageSize={pageSize}
          totalItems={filteredStudents.length}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />

      </div>

      {/* Student Drawer */}
      {drawerReportCard && (
        <ReportCardStudentDrawer
          reportCard={drawerReportCard}
          onClose={() => setDrawerReportCard(null)}
          onOpenFullPreview={(rc) => {
            setDrawerReportCard(null);
            setPreviewReportCard(rc);
          }}
          onDownloadPDF={handleDownloadSinglePDF}
          onPublish={handlePublishSingle}
          onSaveRemarksSuccess={loadReportCards}
          showSuccessNotification={showSuccessNotification}
          showErrorNotification={showErrorNotification}
        />
      )}

    </div>
  );
};

export default ReportCardWorkspace;
